export const patientDocumentation = `
## Fields Specification

| Field | Required | Editable | Type | Formatting | Max Length | Description |
|-------|----------|----------|------|------------|------------|-------------|
| mrnNumber | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | - | - | Medical Record Number, must be unique per account |
| firstName | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | - | - | Patient's first name |
| lastName | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | - | - | Patient's last name |
| email |   | <span class="checkmark">✓</span> | String | Email format | - | Patient's email address (stored lowercase) |
| **phone** |   | <span class="checkmark">✓</span> | **Object** | US phone format | - | Primary contact number |
| <span class="nested-field">phone.phone</span> |   | <span class="checkmark">✓</span> | String | Digits only | - | Phone number (digits only, no formatting) |
| **preferredPhone** |   | <span class="crossmark">✗</span> | **Object** | US phone format | - | Patient's preferred contact (read-only). Displays the primary telephone number as recorded by the patient and cannot be modified through the administrative interface. |
| <span class="nested-field">preferredPhone.phone</span> |   | <span class="crossmark">✗</span> | String | Digits only | - | Preferred phone number (digits only) |
| dateOfBirth | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | DateTimeOffset | ISO 8601 | - | Birth date, must be in the past |
| genderAtBirth | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Enum | - | - | Options: male, female |
| drugAllergies |   | <span class="checkmark">✓</span> | String | - | 1000 chars | Free text for allergies |
| hasDrugAllergies |   | <span class="checkmark">✓</span> | Boolean | - | - | Whether patient has drug allergies |
| patientLanguage |   | <span class="checkmark">✓</span> | Enum | - | - | Options: english, spanish, unknown |
| <span class="added">race</span> |   | <span class="checkmark">✓</span> | Enum | - | - | Patient's race/ethnicity. Options: non-hispanic-white, hispanic-latino, african-american-black, asian, american-indian-alaska-native, native-hawaiian-pacific-islander, middle-eastern-north-african, multiracial-other |
| **address** |   | <span class="checkmark">✓</span> | **Object** | - | - | Full address object |
| <span class="nested-field">address.name</span> |   | <span class="checkmark">✓</span> | String | - | - | Address name/label |
| <span class="nested-field">address.streetAddress</span> |   | <span class="checkmark">✓</span> | String | - | - | Street address |
| <span class="nested-field">address.suite</span> |   | <span class="checkmark">✓</span> | String | - | - | Suite/apartment number |
| <span class="nested-field">address.city</span> |   | <span class="checkmark">✓</span> | String | - | - | City name |
| <span class="nested-field">address.state</span> |   | <span class="checkmark">✓</span> | String | - | - | US state code |
| <span class="nested-field">address.zipCode</span> |   | <span class="checkmark">✓</span> | String | - | - | ZIP code |
| smsOptIn |   | <span class="checkmark">✓</span> | Boolean | - | - | SMS consent |
| hasEmailSubscription |   | <span class="checkmark">✓</span> | Boolean | - | - | Email subscription status |

## Validation Rules

1. **MRN Number**
   - Required field
   - Must be unique within the account
   - Cannot be empty string

2. **Names**
   - First and last name are required
   - Cannot be empty strings
   - Trimmed automatically

3. **Email**
   - Must be valid email format if provided
   - Optional field
   - Converted to lowercase automatically
   - Trimmed automatically

4. **Phone Numbers**
   - US format validation
   - Stored as digits only (no formatting characters)
   - Optional fields
   - Preferred phone is read-only

5. **Date of Birth**
   - Required field
   - Must be a valid DateTimeOffset
   - Must be less than current UTC time

6. **Gender at Birth**
   - Required field
   - Must be valid enum value: "male" or "female"

7. **Patient Language**
   - Optional field
   - Must be valid enum value: "english", "spanish", or "unknown"

<span class="added">8. **Race**
   - Optional field
   - Must be valid enum value: "non-hispanic-white", "hispanic-latino", "african-american-black", "asian", "american-indian-alaska-native", "native-hawaiian-pacific-islander", "middle-eastern-north-african", or "multiracial-other"</span>

9. **Drug Allergies**
   - Maximum length: 1000 characters
   - Required if hasDrugAllergies is true

10. **Address**
   - Required for non-Athena patients
   - All individual address fields are optional
   - State must be valid US state code if provided

## Permissions

### Patient Creation
Required permission: \`PATIENT_CREATE\`

### Patient Reading
Required permission: \`PATIENT_GET\`

### Patient Updating
Required permission: \`PATIENT_PATCH\`

### Patient Deletion
Required permission: \`PATIENT_DELETE\`

## API Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| PUT | \`/v1.0/accounts/{accountUUID}/patients\` | Create new patient |
| GET | \`/v1.0/accounts/{accountUUID}/patients/{patientUUID}\` | Get patient details |
| PATCH | \`/v1.0/accounts/{accountUUID}/patients/{patientUUID}\` | Update patient |
| DELETE | \`/v1.0/accounts/{accountUUID}/patients/{patientUUID}\` | Delete patient |
| GET | \`/v1.0/accounts/{accountUUID}/patients\` | List patients |

## Request/Response Examples

### Create Patient (PUT)
**Request Body:**
\`\`\`json
{
  "mrnNumber": "MRN123456",
  "firstName": "John",
  "lastName": "Doe",
  "email": "john.doe@example.com",
  "phone": {
    "phone": "1234567890"
  },
  "preferredPhone": {
    "phone": "1234567890"
  },
  "dateOfBirth": "1990-01-01T00:00:00Z",
  "genderAtBirth": "male",
  "drugAllergies": "Penicillin",
  "hasDrugAllergies": true,
  "patientLanguage": "english",
  <span class="added">"race": "hispanic-latino"</span>,
  "address": {
    "name": "",
    "streetAddress": "123 Main St",
    "suite": "Apt 4B",
    "city": "New York",
    "state": "NY",
    "zipCode": "10001"
  },
  "smsOptIn": true,
  "hasEmailSubscription": false
}
\`\`\`

**Success Response (201 Created):**
\`\`\`json
{
  "uuid": "patient-uuid-here",
  "mrnNumber": "MRN123456",
  "firstName": "John",
  "lastName": "Doe",
  "email": "john.doe@example.com",
  "phone": {
    "phone": "1234567890"
  },
  "preferredPhone": {
    "phone": "1234567890"
  },
  "dateOfBirth": "1990-01-01T00:00:00Z",
  "genderAtBirth": "male",
  "drugAllergies": "Penicillin",
  "hasDrugAllergies": true,
  "patientLanguage": "english",
  <span class="added">"race": "hispanic-latino"</span>,
  "address": {
    "name": "",
    "streetAddress": "123 Main St",
    "suite": "Apt 4B",
    "city": "New York",
    "state": "NY",
    "zipCode": "10001"
  },
  "smsOptIn": true,
  "hasEmailSubscription": false,
  "createdAt": "2024-01-01T12:00:00Z",
  "updatedAt": "2024-01-01T12:00:00Z"
}
\`\`\`

### Update Patient (PATCH)
**Request Body (JSON Patch format):**
\`\`\`json
[
  {
    "op": "replace",
    "path": "/email",
    "value": "new.email@example.com"
  },
  {
    "op": "replace",
    "path": "/phone/phone",
    "value": "9876543210"
  },
  <span class="added">{
    "op": "replace",
    "path": "/race",
    "value": "asian"
  }</span>
]
\`\`\`

**Success Response (200 OK):**
\`\`\`json
{
  "uuid": "patient-uuid-here",
  "mrnNumber": "MRN123456",
  "firstName": "John",
  "lastName": "Doe",
  "email": "new.email@example.com",
  "phone": {
    "phone": "9876543210"
  },
  // ... other fields
  "updatedAt": "2024-01-01T13:00:00Z"
}
\`\`\`

## HTTP Status Codes

| Status Code | Scenario | Description |
|-------------|----------|-------------|
| 200 OK | Successful GET/PATCH | Patient retrieved/updated successfully |
| 201 Created | Successful PUT | Patient created successfully |
| 400 Bad Request | Validation errors | Invalid request body or validation failures |
| 404 Not Found | Resource not found | Patient or account not found |
| 409 Conflict | MRN already exists | MRN number already exists for the account |
| 500 Internal Server Error | Server error | Unexpected server error |

## Error Response Format

**Validation Errors (400 Bad Request):**
\`\`\`json
{
  "errors": {
    "mrnNumber": ["MRN number is required"],
    "email": ["Invalid email format"],
    "dateOfBirth": ["Date of birth must be in the past"]
  }
}
\`\`\`

**Not Found Error (404):**
\`\`\`json
{
  "message": "Patient not found"
}
\`\`\`

## Query Parameters

- \`?validate-only=true\` - Validate request without creating/updating (returns 200 OK if valid)
`;
